import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useOrganization } from './useOrganization';
import { DEFAULT_PIPELINE_COLUMNS } from '../constants/pipelineColumns';
import type { PipelineEntry, PipelineColumnConfig, UsePipelineReturn } from '../types/pipeline';
import { toast } from 'sonner';

export const usePipeline = (missionId: number): UsePipelineReturn => {
  const { org } = useOrganization();
  const [entries, setEntries] = useState<PipelineEntry[]>([]);
  const [columns, setColumns] = useState<PipelineColumnConfig[]>(DEFAULT_PIPELINE_COLUMNS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPipeline = useCallback(async () => {
    if (!missionId || isNaN(missionId)) return;
    setIsLoading(true);
    setError(null);

    try {
      // Run both fetches in parallel (avoids sequential loading — Pitfall 4)
      const [matchesResult, missionResult] = await Promise.all([
        supabase
          .from('matches')
          .select(`
            id,
            candidate_id,
            match_score,
            placement_status,
            candidates (
              id,
              full_name,
              summary_json,
              availability_status,
              availability_date
            )
          `)
          .eq('mission_id', missionId)
          .not('placement_status', 'is', null)
          .order('created_at', { ascending: true }),
        supabase
          .from('missions')
          .select('pipeline_config')
          .eq('id', missionId)
          .single(),
      ]);

      if (matchesResult.error) throw matchesResult.error;

      // Map DB rows to domain PipelineEntry objects
      const mapped: PipelineEntry[] = (matchesResult.data || []).map((row: any) => {
        const cand = row.candidates ?? {};
        const sj: Record<string, unknown> = typeof cand.summary_json === 'string'
          ? (() => { try { return JSON.parse(cand.summary_json); } catch { return {}; } })()
          : (cand.summary_json ?? {});

        return {
          matchId: row.id as number,
          candidateId: row.candidate_id as number,
          fullName: (cand.full_name as string) || 'Inconnu',
          currentTitle: (sj.Suggested_title as string) || (sj.role as string) || '',
          matchScore: (row.match_score as number) || 0,
          availabilityStatus: (cand.availability_status as string) || null,
          availabilityDate: (cand.availability_date as string) || null,
          stage: row.placement_status as string,
        };
      });

      setEntries(mapped);

      // Load custom column config if present in mission
      if (!missionResult.error && missionResult.data) {
        const config = (missionResult.data as any).pipeline_config;
        if (config?.columns && Array.isArray(config.columns) && config.columns.length > 0) {
          setColumns([...config.columns].sort((a: PipelineColumnConfig, b: PipelineColumnConfig) => a.position - b.position));
        } else {
          setColumns(DEFAULT_PIPELINE_COLUMNS);
        }
      } else {
        setColumns(DEFAULT_PIPELINE_COLUMNS);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors du chargement du pipeline';
      setError(msg);
      console.error('[usePipeline] fetchPipeline error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [missionId]);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  const updateStage = useCallback(async (matchId: number, newStage: string): Promise<void> => {
    // Optimistic update — update local state BEFORE async call (PIPE-03)
    const previousEntries = entries;
    setEntries(prev => prev.map(e => e.matchId === matchId ? { ...e, stage: newStage } : e));

    const { error: updateError } = await supabase
      .from('matches')
      .update({ placement_status: newStage })
      .eq('id', matchId)
      .eq('organization_id', org!.id); // defense-in-depth (RLS already handles isolation)

    if (updateError) {
      // Rollback on error
      setEntries(previousEntries);
      toast.error('Erreur lors du déplacement. Veuillez réessayer.');
      console.error('[usePipeline] updateStage error:', updateError);
    }
  }, [entries, org]);

  const addToPipeline = useCallback(async (candidateId: number): Promise<void> => {
    // Guard: do NOT update if candidate already has a placement_status (Pitfall 2)
    const alreadyInPipeline = entries.some(e => e.candidateId === candidateId);
    if (alreadyInPipeline) {
      return;
    }

    // Fetch only the best match row (highest score) to avoid adding duplicates
    // when the same candidate was matched in multiple tasks for this mission
    const { data: bestMatch, error: fetchErr } = await supabase
      .from('matches')
      .select('id')
      .eq('mission_id', missionId)
      .eq('candidate_id', candidateId)
      .is('placement_status', null)
      .order('match_score', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (fetchErr || !bestMatch) {
      toast.error("Impossible d'ajouter ce candidat. Veuillez réessayer.");
      console.error('[usePipeline] addToPipeline fetch error:', fetchErr);
      return;
    }

    const { error: addError } = await supabase
      .from('matches')
      .update({ placement_status: 'identified' })
      .eq('id', bestMatch.id);

    if (addError) {
      toast.error("Impossible d'ajouter ce candidat. Veuillez réessayer.");
      console.error('[usePipeline] addToPipeline error:', addError);
      return;
    }

    toast.success("Candidat ajouté au pipeline dans 'Identifié'");
    await fetchPipeline();
  }, [entries, missionId, fetchPipeline]);

  return {
    entries,
    columns,
    isLoading,
    error,
    updateStage,
    addToPipeline,
    refresh: fetchPipeline,
  };
};
